import { Table } from "react-bootstrap";
import { SurveysHeaderHistory, SurveyHistoryFields, SurveysHistoryCSV } from "../types";
import { AppTableRowActions } from "../../../components/AppTable/AppTableRowActions";
import { SectionPermissionTypeEnum } from "../../../types/SectionPermissionTypesEnum";
import { convertIntUnixToFormateDate } from "../../../utils/dateUtils";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import { useCurrentClient } from "../../auth/api/getCurrentClient";
import { COLOR } from "../../../config/constraits";
import { truncateText } from "../../../utils/textUtils";
import { useTranslation } from "react-i18next";
import { saveCSV } from "../../../services/api/csvManagmentService";
import ModalGlobal from "../../../components/AppModal/ModalGlobal";
import AlertMessage from "../../../components/AlertMessage";
import ExpandableTags from "../../../components/common/ExpandableTags/ExpandableTags";
import { KeyValuePair } from "../../../types/KeyValuePair";
import AppTablePagination from "../../../components/AppTable/AppTablePagination";
import { useAlert } from "../../../hooks/useAlert";
import { useModal } from "../../../hooks/useModal";
import { ApiErrorResponse } from "../../../types/ApiErrorResponse";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import TextPill from "../../../components/TextPill";
import { deleteSurvey } from "../api/getHistory";
import { useHistoryCsvColumns } from "../api/getHistoryCsvColumns";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { UseSelectBasicObject } from "../../../types/UseSelectBasicObject";

const MAX_TEXT_LENGTH = 32;

interface HistoricalTableProps {
  historyHeader: SurveysHeaderHistory;
  historyData: SurveyHistoryFields[];
  onOrderChange: (orderBy: string) => void;
  currentPage: number;
  pageSize: number;
  orderBy: string;
  orderDirection: 'asc' | 'desc';
  totalPages: number;
  goToDetails: (commentId: number) => void;
}
export interface ClientDto {
    SelectedData: SurveyHistoryFields[];
    OrderByField: 0;
}

export default function HistoricalTable({ historyHeader, historyData, currentPage, pageSize, orderBy, orderDirection, onOrderChange, totalPages, goToDetails }: HistoricalTableProps) {
    const { data: currentClient } = useCurrentClient();
    const { t } = useTranslation(['dashboard','common']);
    const headerItemsArray = Object.values(historyHeader).filter(item => item != null);

    //alerta mensaje
    const { alert, hideAlert, isVisible, showAlert } = useAlert({ fromLocation: true });
    const selectedFilters = useSelector((state: RootState) => state.filters.dashboard.selectedFilters);
    const queryClient = useQueryClient();

	const [selectedCsvFields, setSelectedCsvFields] = useState<string[]>([]);

	const { data: csvColumns } = useHistoryCsvColumns();
	const csvColumnsArray = csvColumns ?? [];

	const exportFieldOptions: UseSelectBasicObject[] = csvColumnsArray
    .map((column) => ({ id: column.fieldName, name: column.description }));

	const handleExportCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value, checked } = event.target;
		setSelectedCsvFields((prev) =>
			checked ? [...prev, value] : prev.filter((f) => f !== value)
		);
	};

	const handleSelectAllCsvFields = (checked: boolean) => {
		setSelectedCsvFields(checked ? exportFieldOptions.map((f) => String(f.id)) : []);
	};

	const handleModalExportCsv = async () => {
		setSelectedCsvFields(exportFieldOptions.map((f) => String(f.id))); // default: all selected
		openExportCsvModal(
			historyData,
			t("dashboard:csv.modalShowHistoricalConfirmationCSVTitle"),
			t("dashboard:csv.modalShowHistoricalConfirmationCSVDescription", { description: "" }),
			"exportCsv",
			""
		);
	};

    const { showModal, modalData, errorModal, endDate, openModal: openExportCsvModal, closeModal, isLoading } = useModal<SurveyHistoryFields[]>();
    const {
        showModal: showDeleteModal,
        modalData: deleteModalData,
        errorModal: deleteErrorModal,
        currentItem: surveyToDelete,
        closeModal: closeDeleteModal,
        isLoading: isDeleting,
        setLoading: setDeleteLoading,
    } = useModal<SurveyHistoryFields>();

	const handleConfirmExportCSV = async () => {
    try {
        const filteredHeader = csvColumnsArray.filter(
            (column) => selectedCsvFields.includes(column.fieldName)
        );

        const body: SurveysHistoryCSV = {
            surveyHistoryFields: historyData,
            itemsHeader: filteredHeader,
            selectedFilters: selectedFilters
        };
        await saveCSV(body);
        showAlert({
            message: t("dashboard:csv.succesDowloadCSV"),
            type: 'success',
            duration: 8000
        });
    } catch (error) {
        const fetchError = error as ApiErrorResponse;
        const errorMessage = fetchError?.message;

        showAlert({
            message: errorMessage?.toString() ?? t("dashboard:csv.errorDowloadCSV"),
            type: 'danger',
            duration: 8000
        });
    }
    closeModal();
};
 
    const handleConfirmDelete = async () => {
        if (!surveyToDelete) return;
        try {
            setDeleteLoading(true);
            await deleteSurvey(surveyToDelete.surveyId);
            await queryClient.invalidateQueries({ queryKey: ["history"] });
            showAlert({
                message: t("dashboard:historical.deleteSurveySuccess"),
                type: 'success',
                duration: 5000
            });
            closeDeleteModal();
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            showAlert({
                message: fetchError?.message?.toString() ?? t("dashboard:historical.deleteSurveyError"),
                type: 'danger',
                duration: 8000
            });
            setDeleteLoading(false);
        }
    };

	const getFeelingText = (feeling: number): string => {
		return feeling < (currentClient?.neutralReference || 2) 
			? "Negatiu" 
			: feeling > (currentClient?.positiveReference || 4) 
				? "Positiu"
				: "Neutre";
	}
	const getFeelingColor = (feeling: number): string => {
		return feeling < (currentClient?.neutralReference || 2) 
			? COLOR.VERMELL_LIGHT
			: feeling > (currentClient?.positiveReference || 4) 
				? COLOR.VERD_LIGHT
				: COLOR.TARONJA_LIGHT;
	}
    const hasSurveyAlert = (data: SurveyHistoryFields): boolean => {
        return data.hasAlert === true;
    };
  	return (
      	<div className="container-fluid">
			{isVisible && (
				<AlertMessage
					message={alert!.message}
					type={alert!.type}
					onClose={hideAlert}
				/>
			)} 
			<ModalGlobal
				show={showModal}
				onClose={closeModal}
				title={modalData.title}
				contentType={modalData.type}
				subtitle={modalData.subtitle}
				subtitle2={modalData.subtitle2}
				saveText={t("common:modalActionOK")}
				closeText={t("common:modalActionNOTOK")}
				error={errorModal?.endDate || errorModal?.errorModal}
				endDate={endDate}
				onSave={() => handleConfirmExportCSV()}
				isLoading={isLoading}
				optionsMap={exportFieldOptions}
				showOptions={true}
				selectedOptions={selectedCsvFields}
				handleCheckboxChange={handleExportCheckboxChange}
    			handleSelectAllChange={handleSelectAllCsvFields}
			/>
			<ModalGlobal
				show={showDeleteModal}
				onClose={closeDeleteModal}
				title={deleteModalData.title}
				contentType={deleteModalData.type}
				subtitle={deleteModalData.subtitle}
				subtitle2={deleteModalData.subtitle2}
				saveText={t("common:modalActionOK")}
				closeText={t("common:modalActionNOTOK")}
				error={deleteErrorModal?.errorModal}
				onSave={handleConfirmDelete}
				isLoading={isDeleting}
			/> 
			<div style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
				<Table className="mt-3 text-center mb-0" style={{ minWidth: "max-content", width: "100%" }}>
					<thead>
						<tr>
							{headerItemsArray.filter(header => !!header).map((headerItem, index) => {
								return (
								<th key={index} className="text-center" style={{ borderTop: "none", borderBottom: "1px solid black" }}>
									<button
										onClick={() => onOrderChange(headerItem.fieldName) }
												style={{ background: 'none', border: 'none', fontWeight: 'bold', whiteSpace: "nowrap",  }}
										>
										{t(headerItem.description)}{' '}
										{orderBy === headerItem.fieldName && (
											orderDirection === 'asc' ? <FaSortUp /> : <FaSortDown />
										)}
									</button>
								</th>
								);
							})}
							<th className="text-center" style={{ borderTop: "none", borderBottom: "1px solid black" }}>{t("common:actions") }</th>
						</tr>
					</thead>
					<tbody>
						{historyData.map((data, index) => (
							<tr key={index}>
								{Object.keys(data).filter(fieldName => fieldName.includes("extraField")).map((key, index) => {
									const extraField = data[key as keyof SurveyHistoryFields] as string;
									if (index < headerItemsArray.filter(header => !!header && (header.fieldName.includes("ExtraField") || header.fieldName == "FieldCreateDate" || header.fieldName.toLowerCase() == "nps")).length) {
										if (headerItemsArray[index]?.isDateField) {
											return <td key={index} style={{ fontSize: "14px", whiteSpace: "nowrap", verticalAlign: "middle" }}>{convertIntUnixToFormateDate(parseInt(extraField))}</td>
										}
										return (
											<td key={index} style={{ fontSize: "14px", whiteSpace: "nowrap", verticalAlign: "middle" }}>{extraField}</td>
										);
									}
								})}
								<td key={index} style={{ fontSize: "14px", whiteSpace: "nowrap", verticalAlign: "middle" }}>
									{convertIntUnixToFormateDate(parseInt(data.answerDate))}
								</td>
								<td style={{ verticalAlign: "middle" }}>
									<TextPill
										label={getFeelingText(parseInt(data.feeling))}
										backgroundColor={getFeelingColor(parseInt(data.feeling))}
										textColor={parseInt(data.feeling) < (currentClient?.neutralReference ?? 2) ? "white" : "black"}
										minWidth="70px"
										fontWeight={400}
									/>
								</td>
								<td style={{ verticalAlign: "middle" }}>
									{(() => {
										const isAlert = hasSurveyAlert(data);
										return (
									<TextPill
										label={isAlert ? t("common:yes") : t("common:no")}
										backgroundColor={isAlert ? "#ff7576" : "white"}
										textColor={isAlert ? "white" : "black"}
										minWidth="70px"
										fontWeight={400}
									/>
										);
									})()}
								</td>
								<td style={{ verticalAlign: "middle" }}>
									<ExpandableTags tags={data.topics.map((tag: KeyValuePair) => {
										const neutral = currentClient?.neutralReference ?? 4;
										const positive = currentClient?.positiveReference ?? 8;
										const feelingNote = tag.value as number | null;

										let borderColor = "";
										if (feelingNote == null) {
										borderColor = "#000";
										}
										else if (feelingNote < neutral) {
										borderColor = COLOR.VERMELL;
										} else if (feelingNote > positive) {
										borderColor = COLOR.VERD;
										} else {
										borderColor = COLOR.TARONJA;
										}

										return {
										text: tag.key as string,
										style: {
											borderColor
										}
										}
									})} />
								</td>
								<td style={{ fontSize: "14px", whiteSpace: "nowrap", textAlign: "left", maxWidth: 250, verticalAlign: "middle" }}>
									{data.comment && truncateText(data.comment, MAX_TEXT_LENGTH)}
								</td>
								<td style={{ verticalAlign: "middle" }}>
									<AppTableRowActions
										object={{ ...data, id: data.surveyId }}
										sectionPermission={SectionPermissionTypeEnum.GLOBAL_DASHBOARD}
										onView={() => goToDetails(data.surveyId)}
										t={t}
									/>
								</td>
							</tr>
						))}
					</tbody>
				</Table>
			</div>
			<AppTablePagination
				currentPage={currentPage}
				totalPages={totalPages}
				pageSize={pageSize}
				showExportLink={true}
				onExportCsv={handleModalExportCsv}
				selectorType="historicalData"
				asTableRow={false}
			/>
		</div>
	);
}


